import * as React from 'react';
import * as ReactDom from 'react-dom';
import BlendMode from '../data/BlendMode';
import { storeDispatch } from '../state/Store';
import { reportIssue } from '../utils/Tracker';
import Layer from '../data/Layer';
import { Effect } from '@beatflyer/shaders-lib';
import { SignalSource } from 'data/SignalSource';
import { SplitLayerCommand } from 'state/commands/SplitLayerCommand';
import { newUUID } from 'utils';
const createEffect = require('@beatflyer/shaders-lib').createEffect;

enum SplitMode {
  Luminosity = 'Luminosity',
  Color = 'Color',
  BlendAdd = 'Blend Add',
  BlendMultiply = 'Blend Multiply',
  BlendScreen = 'Blend Screen'
}

const SPLIT_TYPES: SplitMode[] = [
  SplitMode.Luminosity,
  SplitMode.Color,
  SplitMode.BlendAdd,
  SplitMode.BlendMultiply,
  SplitMode.BlendScreen
];

const PIXI = (window as any).PIXI;

class PixiLayer {
  renderer: PIXI.WebGLRenderer;
  stage: PIXI.Container;
  sprite: PIXI.Sprite;
  constructor(texture: PIXI.Texture) {
    this.renderer = new PIXI.Renderer({
      width: texture.width,
      height: texture.height,
      transparent: true
    });
    this.renderer.view.style.height = '140px';
    this.renderer.view.style.width = 'auto';

    this.stage = new PIXI.Container();
    this.sprite = new PIXI.Sprite(texture);
    this.stage.addChild(this.sprite);
  }
  render() {
    this.renderer.render(this.stage);
  }
}

interface Props {
  layer: Layer;
  onCancel: () => void;
}

interface State {
  mode: SplitMode;
  color: number;
  threshold: number;
  removeFromButton: boolean;
}

export default class extends React.Component<Props, State> {
  state = {
    mode: SplitMode.Luminosity,
    color: 0,
    threshold: 0.5,
    removeFromButton: true
  };
  topLayer: PixiLayer;
  bottomLayer: PixiLayer;
  topFilter: Effect;
  bottomFilter: Effect;
  createPixiLayers() {
    const texture = this.props.layer.currentFrame.texture!;
    const el = ReactDom.findDOMNode(this) as HTMLElement;

    this.topLayer = new PixiLayer(texture);
    el.querySelector('.layers .top .layer')!.appendChild(
      this.topLayer.renderer.view
    );

    this.bottomLayer = new PixiLayer(texture);
    el.querySelector('.layers .bottom .layer')!.appendChild(
      this.bottomLayer.renderer.view
    );

    this.createFilters();
  }
  createFilters() {
    const isSplittingPixels =
      this.state.mode === SplitMode.Luminosity ||
      this.state.mode === SplitMode.Color;
    let name;
    switch (this.state.mode) {
      case SplitMode.Luminosity:
        name = 'split-layer-luminosity';
        break;
      case SplitMode.Color:
        name = 'split-layer-color';
        break;
      case SplitMode.BlendAdd:
        name = 'split-layer-color-add';
        break;
      case SplitMode.BlendMultiply:
        name = 'split-layer-color-multiply';
        break;
      case SplitMode.BlendScreen:
        name = 'split-layer-color-screen';
        break;
      default:
        reportIssue('Could not find filter name for mode: ' + this.state.mode);
        break;
    }

    this.topFilter = createEffect({ name });
    this.topFilter.id = newUUID();
    this.topFilter.signalSource = SignalSource.Default;
    this.bottomFilter = createEffect({ name });
    this.bottomFilter.id = newUUID();
    this.bottomFilter.signalSource = SignalSource.Default;

    if (isSplittingPixels) {
      this.topFilter.setProperty('threshold', this.state.threshold);
      this.bottomFilter.setProperty('threshold', this.state.threshold);
    }

    if (
      this.state.mode === SplitMode.Color ||
      this.state.mode === SplitMode.BlendAdd
    ) {
      this.topFilter.setProperty('color', this.state.color);
      this.bottomFilter.setProperty('color', this.state.color);
    }

    this.bottomFilter.isInverted = true;

    this.topLayer.sprite.filters = [this.topFilter];
    this.topLayer.render();

    this.bottomLayer.sprite.filters =
      this.state.removeFromButton || !isSplittingPixels
        ? [this.bottomFilter]
        : [];
    this.bottomLayer.render();
  }
  componentDidMount() {
    this.createPixiLayers();
  }
  didChangeMode = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      {
        mode: e.target.value as SplitMode
      },
      () => {
        this.createFilters();
      }
    );
  };
  didChangeColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = parseFloat(e.target.value);
    if (
      this.state.mode === SplitMode.Color ||
      this.state.mode === SplitMode.BlendAdd
    ) {
      this.topFilter.setProperty('color', color);
      this.bottomFilter.setProperty('color', color);
    }
    this.topLayer.render();
    this.bottomLayer.render();
    this.setState({
      color
    });
  };
  didChangeThreshold = (e: React.ChangeEvent<HTMLInputElement>) => {
    const threshold = parseFloat(e.target.value);
    this.topFilter.setProperty('threshold', threshold);
    this.bottomFilter.setProperty('threshold', threshold);
    this.topLayer.render();
    this.bottomLayer.render();
    this.setState({
      threshold
    });
  };
  didChangeRemove = (e: React.ChangeEvent<HTMLInputElement>) => {
    const removeFromButton = e.target.checked;
    if (removeFromButton) {
      this.bottomLayer.sprite.filters = [this.bottomFilter];
    } else {
      this.bottomLayer.sprite.filters = [];
    }
    this.topLayer.render();
    this.bottomLayer.render();
    this.setState({
      removeFromButton
    });
  };
  doSplit = () => {
    let blendMode;
    switch (this.state.mode) {
      case SplitMode.BlendAdd:
        blendMode = BlendMode.Add;
        break;
      case SplitMode.BlendMultiply:
        blendMode = BlendMode.Multiply;
        break;
      case SplitMode.BlendScreen:
        blendMode = BlendMode.Screen;
        break;
      default:
        blendMode = BlendMode.Normal;
        break;
    }
    const bFilter = this.state.removeFromButton ? this.bottomFilter : undefined;
    storeDispatch(
      new SplitLayerCommand(
        this.props.layer.id,
        bFilter?.toObject(),
        this.topFilter.toObject(),
        blendMode
      )
    );
  };
  render() {
    const isSplittingPixels =
      this.state.mode === SplitMode.Luminosity ||
      this.state.mode === SplitMode.Color;
    return (
      <div className="content split">
        <h2>New Layers</h2>
        <div className="layers">
          <div className="top">
            <label>Top</label>
            <div className="layer" />
          </div>
          <div className="bottom">
            <label>Bottom</label>
            <div className="layer" />
          </div>
        </div>
        <div className="controls">
          <div className="option">
            <label>Split by:</label>
            <select value={this.state.mode} onChange={this.didChangeMode}>
              {SPLIT_TYPES.map(type => {
                return (
                  <option value={type} key={type}>
                    {type}
                  </option>
                );
              })}
            </select>
          </div>
          {(this.state.mode === SplitMode.Color ||
            this.state.mode === SplitMode.BlendAdd) && (
            <div className="option">
              <label>Color:</label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.001}
                value={this.state.color}
                onChange={this.didChangeColor}
              />
            </div>
          )}
          {isSplittingPixels && (
            <div className="option">
              <label>Threshold:</label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.001}
                value={this.state.threshold}
                onChange={this.didChangeThreshold}
              />
            </div>
          )}
          {isSplittingPixels && (
            <div className="option">
              <label>Remove from bottom:</label>
              <input
                type="checkbox"
                checked={this.state.removeFromButton}
                onChange={this.didChangeRemove}
              />
            </div>
          )}
        </div>
        <div className="buttons">
          <button className="large secondary" onClick={this.props.onCancel}>
            Cancel
          </button>
          <button
            className="large primary"
            onClick={this.doSplit}
            disabled={
              (this.state.threshold === 0 || this.state.threshold === 1) &&
              this.state.mode === SplitMode.Luminosity
            }
          >
            Split
          </button>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/split-layer/Split.tsx